<template>
  <div>
    <main-layout
      controlsBg="light"
      :hasControlsBar="false"
      :breadcrumb="getBreadcrumb">
      <div class="container">
        <grid-row column-align="center">
          <grid-col col-size="4">
            <h1 class="header margin-top-xl">
              <div>
                <chevron
                  @click="$router.push({name: origin})"/>
                {{ getTitle }}
              </div>
              <GoToTagsAndCategorical
                origin="AggregateGradualVariable"/>
            </h1>
            <alert
              v-if="alertVisible"
              :message="alertMessage"
              :type="alertType">
            </alert>
          </grid-col>
        </grid-row>
        <form v-on:submit.prevent v-on:keypress.enter.prevent="onSave">
          <grid-row column-align="center">
            <grid-col col-size="4">
              <div class="variable-input-field">
                <label-variable-selector
                  :disabled="isEdition()"
                  class="margin-right-s"
                  :label="$t('aggregateGradualVariable.selectVariable')"
                  :error="variableError"
                  :errorMessage="$t('commons.mandatoryFieldMessage')"
                  v-model="aggregateVariable"
                  v-on:tagsChanged="tagsChanged"
                  :variables="getVariablesMinimal"
                  :taggedVariables="getTaggedVariables"
                  :tree="getTagsTree" />
              </div>
            </grid-col>
          </grid-row>
          <grid-row column-align="center">
            <grid-col col-size="4">
              <div class="variable-input-field">
                <label-select
                  :disabled="isEdition()"
                  :label="$t('aggregateGradualVariable.timeGap')"
                  v-model="timeGap"
                  :options="timeGapSelectOptions"
                  :error="timeGapError"
                  :errorMessage="$t('commons.mandatoryFieldMessage')"
                  form-size="m"
                  class="margin-right-s">
                </label-select>
              </div>
            </grid-col>
          </grid-row>
          <grid-row column-align="center">
            <grid-col col-size="4">
              <div class="variable-input-field variable-input-field-name">
                <label-input
                  :label="$t('aggregateVariable.name')"
                  v-model="name"
                  :error="nameError"
                  :errorMessage="$t('commons.mandatoryFieldMessage')"
                  class="margin-right-s">
                </label-input>
              </div>
              <div class="variable-input-field variable-input-field-unit">
                <label-input
                  :label="$t('aggregateVariable.unit')"
                  v-model="unit">
                </label-input>
              </div>
            </grid-col>
          </grid-row>
          <grid-row
            col-size="4"
            column-align="center">
            <grid-col
              col-size="4">
              <category-selector
                :label="$t('variableManagement.category')"
                :categories="getCategories"
                v-model="variableCategory">
              </category-selector>
            </grid-col>
          </grid-row>
          <grid-row
            col-size="4"
            column-align="center">
            <tags-editor
              :key="tagTreeKey"
              class="variable-input-field variable-input-field-name"
              :tree="getTagsTree"
              v-model="tags"/>
          </grid-row>
          <grid-row column-align="center">
            <grid-col col-size="4">
              <div class="align-right margin-bottom-xxl">
                <checkbox
                  id="historic"
                  class="margin-right-s"
                  :label="$t('aggregateMetricalVariable.calculateHistoricalData')"
                  v-on:change="historicalActivatedChanged"
                  :checked="false"
                  check-size="s"
                  :hidden="this.isEdition()"/>
              </div>
            </grid-col>
          </grid-row>
          <grid-row column-align="center">
            <grid-col col-size="4">
              <div class="align-right margin-bottom-l">
                <btn-pill
                  :btnText="$t('commons.cancel')"
                  v-on:click="onCancel"
                  btn-size="m"
                  btn-type="secondary">
                </btn-pill>
                <btn-pill
                  :btnText="$t('commons.save')"
                  v-on:click="onSave"
                  btn-size="m">
                </btn-pill>
              </div>
            </grid-col>
          </grid-row>
        </form>
      </div>
    </main-layout>
  </div>
</template>

<script>
import MainLayout from '../MainLayout';
import GridRow from '@/components/shared/grid/GridRow';
import GridCol from '@/components/shared/grid/GridCol';
import LabelSelect from '@/components/shared/forms/LabelSelect';
import Alert from '@/components/shared/alert/Alert';
import BtnPill from '@/components/shared/buttons/BtnPill';
import {mapActions, mapGetters} from 'vuex';
import LabelInput from '@/components/shared/forms/LabelInput';
import TagsEditor from '@/components/variable/TagsEditor';
import LabelVariableSelector from '@/components/variable/LabelVariableSelector';
import Chevron from '@/components/shared/Chevron';
import GoToTagsAndCategorical from '@/components/variable/GoToTagsAndCategorical';
import CategorySelector from '@/components/variable/CategorySelector';
import aggregateDefs from '@/layouts/variables/aggregateVariableDefs';
import variableApi from '@/api/variable';
import variableSelectorUtils from '@/layouts/variableSelectorUtils';
import Checkbox from '@/components/shared/forms/Checkbox';

export default {
  components: {
    LabelVariableSelector,
    TagsEditor,
    MainLayout,
    GridCol,
    GridRow,
    Alert,
    BtnPill,
    LabelSelect,
    LabelInput,
    Chevron,
    CategorySelector,
    GoToTagsAndCategorical,
    Checkbox
  },
  mixins: [
    variableSelectorUtils
  ],
  data () {
    return {
      origin: 'AggregateGradualVariableList',
      editAggregateGradualVariable: {},
      alertVisible: false,
      alertMessage: '',
      alertType: '', // error, warning, success
      /* Form fields */
      aggregateVariable: undefined,
      timeGap: {label: ' '},
      name: '',
      unit: '',
      variableCategory: null,
      tags: [],
      /* Form field errors */
      variableError: false,
      timeGapError: false,
      nameError: false,
      timeGapSelectOptions: [
        {
          label: this.$t('aggregateGradualVariable.oneMinute'),
          value: 60
        },
        {
          label: this.$t('aggregateGradualVariable.oneHour'),
          value: 3600
        },
        {
          label: this.$t('aggregateGradualVariable.oneDay'),
          value: 86400
        },
        {
          label: this.$t('aggregateGradualVariable.oneMonth'),
          value: 2592000
        }
      ],
      variablesMinimal: [],
      taggedVariables: [],
      historicalActivated: false,
      tagTreeKey: 0
    };
  },
  created () {
    variableApi.getMinimalVariables(this).then(data => {
      this.taggedVariables = this.variablesMinimal = data.body.sort((a, b) => {
        return a.label.localeCompare(b.label, undefined, {numeric: true, sensitivity: 'base'});
      });
      if (this.isEdition()) {
        this.aggregateVariable = getVarById(this.getVariablesMinimal, this.editAggregateGradualVariable.varOneId);
        this.timeGap = this.timeGapSelectOptions.find(option => option.value === this.editAggregateGradualVariable.timeGap);
        this.name = this.editAggregateGradualVariable.name;
        this.unit = this.editAggregateGradualVariable.unit;
        this.tags = this.editAggregateGradualVariable.tags;
        this.tagTreeKey++;
      }
      this.retrieveTagsTree({ context: this });
    });

    this.editAggregateGradualVariable = this.$route.params;
    this.retrieveCategories({ context: this }).then(() => {
      if (this.isEdition()) {
        this.variableCategory = getCategoryById(this.getCategories, this.editAggregateGradualVariable.categoryId);
      }
    });

    // Params
    const origin = this.$route.query.origin;
    if (origin) {
      this.origin = origin;
    }
  },
  computed: {
    ...mapGetters([
      'getTagsTree',
      'getCategories'
    ]),
    getBreadcrumb () {
      switch (this.origin) {
        case 'AggregateGradualVariableList':
        default:
          return [
            { name: this.$t('menu.workspace'), link: '/workspace' },
            { name: this.$t('aggregateVariable.title'), link: '/aggregate-variable/menu' },
            { name: this.$t('aggregateVariable.gradualVariables'), link: '/aggregate-variable/gradual/list' },
            { name: this.getTitle, link: '/aggregate-variable/gradual', disabled: true }
          ];

        case 'VariableTagTree':
          return [
            { name: this.$t('menu.workspace'), link: '/workspace' },
            { name: this.$t('variableManagement.title'), link: '/variable-management' },
            { name: this.$t('variables.taxonomy'), link: '/tags-tree' },
            { name: this.$t('aggregateGradualVariable.editAggregateGradualVariable'), link: '/raw-variable', disabled: true }
          ];

        case 'VariableManagement':
          return [
            { name: this.$t('menu.workspace'), link: '/workspace' },
            { name: this.$t('variableManagement.title'), link: '/variable-management' },
            { name: this.$t('aggregateVariable.addAggregateVariable'), disabled: true }
          ];
      }
    },
    getTitle () {
      return this.isEdition()
        ? this.$t('aggregateGradualVariable.editAggregateGradualVariable')
        : this.$t('aggregateGradualVariable.addAggregateGradualVariable');
    },
    getVariablesMinimal () {
      return this.variablesMinimal;
    },
    getTaggedVariables () {
      return this.taggedVariables;
    }
  },
  methods: {
    ...mapActions([
      'createAggregateVariable',
      'updateAggregateVariable',
      'retrieveTagsTree',
      'retrieveCategories'
    ]),
    isEdition () {
      return this.editAggregateGradualVariable.id !== undefined;
    },
    onCancel () {
      this.$router.push({name: this.origin});
    },
    onSave () {
      clearErrorPrompts(this);
      if (isValidForm(this)) {
        const varId = this.aggregateVariable.id;
        const timeGap = this.timeGap.value;
        const aggregateGradualVariable = {
          varId: varId,
          timeGap: timeGap,
          name: this.name,
          unit: this.unit,
          tags: this.tags,
          goal: aggregateDefs.goal.gradual,
          historic: this.historicalActivated
        };
        if (this.variableCategory !== null) {
          aggregateGradualVariable.categoryId = this.variableCategory.id;
        } else {
          aggregateGradualVariable.categoryId = null;
        }

        if (this.isEdition()) {
          aggregateGradualVariable.id = this.editAggregateGradualVariable.id;
          updateAggregateGradualVariable(this, aggregateGradualVariable);
        } else {
          createAggregateGradualVariable(this, aggregateGradualVariable);
        }
      }
    },
    historicalActivatedChanged: function (event) {
      this.historicalActivated = event;
    }
  }
};

const isValidForm = context => {
  let hasError = false;
  if (!context.aggregateVariable) {
    context.variableError = true;
    hasError = true;
  }

  if (typeof context.timeGap.value === 'undefined') {
    context.timeGapError = true;
    hasError = true;
  }

  if (!context.name || context.name.length < 1) {
    context.nameError = true;
    hasError = true;
  }
  return !hasError;
};

const clearErrorPrompts = context => {
  context.variableError = false;
  context.timeGapError = false;
  context.nameError = false;
};

const showAlert = (context, message, type) => {
  context.alertVisible = true;
  context.alertMessage = message;
  context.alertType = type;
  context.showAlert = true;
  setTimeout(() => {
    context.alertVisible = false;
  }, 2000);
};

const getVarById = (list, ref) => {
  const id = Number(ref);
  for (let i = 0; i < list.length; i++) {
    if (id === list[i].id) {
      return list[i];
    }
  }
};

const getCategoryById = (list, ref) => {
  const id = Number(ref);
  for (let i = 0; i < list.length; i++) {
    if (id === list[i].value) {
      return list[i];
    }
  }
  return null;
};

const createAggregateGradualVariable = (context, aggregateVariable) => {
  context.createAggregateVariable({context: context, aggregateVariable}).then(() => {
    const msg = context.$t('aggregateVariable.successCreation', {name: aggregateVariable.name});
    context.$router.replace({ name: context.origin, params: {alert: msg} });
  }).catch(() => {
    showAlert(context, context.$t('aggregateVariable.errorCreation', {name: aggregateVariable.name}), 'error');
  });
};

const updateAggregateGradualVariable = (context, aggregateVariable) => {
  const msg = context.$t('aggregateVariable.successUpdate', {name: aggregateVariable.name});
  context.updateAggregateVariable({context: context, aggregateVariable}).then(() => {
    context.$router.replace({ name: context.origin, params: {alert: msg} });
  }).catch(() => {
    showAlert(context, context.$t('aggregateVariable.errorUpdate', {name: aggregateVariable.name}), 'error');
  });
};
</script>

<style lang="scss" scoped>
  @import "src/stylesheets/variables";

  .variable-input-field {
    display: inline-block;
    float: left;
    width: 35%;
    margin: 0 0;
  }

  .align-right {
    float: right;
    clear:both;
  }

  .variable-input-field {
    display: inline-block;
  }

  .variable-input-field-name {
    width: 75%;
    margin: 0 0;
  }

  .variable-input-field-unit {
    min-width: 0px;
    width: 25%;
    float: right;
    margin: 0 0;
  }

  .header {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

</style>



// WEBPACK FOOTER //
// src/layouts/variables/AggregateGradualVariable.vue